/**
 * ReplayDualMode - deux tables affichées côte à côte et synchronisées.
 *
 * La lecture est pilotée par la table de gauche ; la position est reportée sur
 * la table de droite via le curseur logique, ce qui tolère des préludes de
 * longueurs différentes (une garde sans n'a ni chien ni écart).
 *
 * Sous une certaine largeur de conteneur, une seule table est affichée avec un
 * bouton de bascule.
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import ReplayPane from './ReplayPane';
import ReplayControls from './ReplayControls';
import ReplayTimeline from './ReplayTimeline';
import type { GameHistory } from '../game/gameHistory';
import { useReplayPlayback } from '../hooks/useReplayPlayback';
import { resolveCursor } from '../game/replayCursor';
import { ContractNames } from '../types/game';
import '../styles/replay.css';

/** Largeur de conteneur en dessous de laquelle on n'affiche qu'une table. */
const DUAL_MIN_WIDTH = 1100;

export interface ReplayPaneSpec {
  history: GameHistory;
  /** Libellé principal (nom de table, d'équipe ou de joueur). */
  label: string;
  /** Complément affiché à droite de l'en-tête (score duplicate par exemple). */
  sublabel?: string;
  masked?: boolean;
  maskMessage?: string;
  maskActionLabel?: string;
  onReveal?: () => void;
}

interface ReplayDualModeProps {
  left: ReplayPaneSpec;
  right?: ReplayPaneSpec | null;
  /** Contrôle affiché dans l'en-tête du volet droit (sélecteur de joueur). */
  rightHeaderExtra?: React.ReactNode;
  /** Contenu affiché à la place de la table droite quand elle n'est pas chargée. */
  rightPlaceholder?: React.ReactNode;
  /** Boutons d'action superposés (partage, export entraînement…). */
  actions?: React.ReactNode;
}

function paneContractLabel(history: GameHistory): string {
  const contractName = ContractNames[history.contract] || '';
  const takerName = history.players[history.taker]?.name || '';
  if (!contractName) return '';
  return takerName ? `${contractName} par ${takerName}` : contractName;
}

export default function ReplayDualMode({
  left,
  right,
  rightHeaderExtra,
  rightPlaceholder,
  actions,
}: ReplayDualModeProps) {
  const playback = useReplayPlayback(left.history);
  const { steps, stepIndex, subStep, maxSubStep, currentStep, cursor } = playback;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [visiblePane, setVisiblePane] = useState<'left' | 'right'>('left');

  // On mesure le conteneur et non le viewport : la zone de replay est déjà
  // réduite par la sidebar de navigation et par la timeline.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setIsNarrow(el.clientWidth < DUAL_MIN_WIDTH);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rightPosition = useMemo(
    () => (right ? resolveCursor(right.history, cursor) : null),
    [right, cursor],
  );

  const stepLabel = useMemo(() => {
    switch (currentStep?.type) {
      case 'distribution':
        return currentStep.label === 'ENCH' ? 'Enchères' : 'Distribution';
      case 'chien':
        return 'Le Chien';
      case 'ecart':
        return "L'Ecart";
      case 'trick':
        return `Pli ${currentStep.trickNumber} — ${subStep}/${maxSubStep} cartes`;
      default:
        return '';
    }
  }, [currentStep, subStep, maxSubStep]);

  const togglePane = useCallback(() => {
    setVisiblePane((p) => (p === 'left' ? 'right' : 'left'));
  }, []);

  const hasRight = Boolean(right) || Boolean(rightPlaceholder);
  const showLeft = !isNarrow || !hasRight || visiblePane === 'left';
  const showRight = hasRight && (!isNarrow || visiblePane === 'right');

  function renderPaneHeader(spec: ReplayPaneSpec | null, extra?: React.ReactNode) {
    return (
      <div className="replay-pane-header">
        <div className="replay-pane-header-main">
          <span className="replay-pane-label">{spec ? spec.label : 'Comparaison'}</span>
          {spec && <span className="replay-pane-contract">{paneContractLabel(spec.history)}</span>}
        </div>
        <div className="replay-pane-header-side">
          {spec?.sublabel && <span className="replay-pane-score">{spec.sublabel}</span>}
          {extra}
        </div>
      </div>
    );
  }

  return (
    <div className="replay-container replay-container--dual" ref={containerRef}>
      <div className="replay-main" ref={playback.gestureRef}>
        <div className="replay-header">
          <div className="replay-header-info">
            <span>Replay synchronisé</span>
            <span>{stepLabel}</span>
          </div>
          {actions && <div className="replay-header-actions">{actions}</div>}
        </div>

        <div className="replay-panes">
          {showLeft && (
            <ReplayPane
              history={left.history}
              stepIndex={stepIndex}
              subStep={subStep}
              header={renderPaneHeader(left)}
              masked={left.masked}
              maskMessage={left.maskMessage}
              maskActionLabel={left.maskActionLabel}
              onReveal={left.onReveal}
            />
          )}

          {showRight && (
            right && rightPosition ? (
              <ReplayPane
                history={right.history}
                stepIndex={rightPosition.stepIndex}
                subStep={rightPosition.subStep}
                header={renderPaneHeader(right, rightHeaderExtra)}
                masked={right.masked}
                maskMessage={right.maskMessage}
                maskActionLabel={right.maskActionLabel}
                onReveal={right.onReveal}
              />
            ) : (
              <div className="replay-pane">
                {renderPaneHeader(null, rightHeaderExtra)}
                <div className="replay-pane-body replay-pane-placeholder">
                  {rightPlaceholder}
                </div>
              </div>
            )
          )}
        </div>

        <ReplayControls
          playback={playback}
          extra={
            isNarrow && hasRight ? (
              <button
                type="button"
                className="replay-btn replay-btn-wide"
                onClick={togglePane}
                title="Afficher l'autre table"
              >
                {visiblePane === 'left'
                  ? `${right?.label ?? 'Autre table'} ⇄`
                  : `⇄ ${left.label}`}
              </button>
            ) : undefined
          }
        />
      </div>

      <ReplayTimeline
        history={left.history}
        steps={steps}
        activeStepIndex={stepIndex}
        onStepClick={playback.handleTimelineClick}
      />
    </div>
  );
}
